import { cn, debounce } from '@/utils';
import React, { memo, ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { Divider } from 'antd';

import type { TabsProps } from 'antd';

import Button from '../button';

interface IProps extends TabsProps {
	title: string;
	tabTitle: {
		id: string;
		title: string;
	};
	description?: {
		registDt: string;
		updateDt: string;
	};
	onDelete?: () => void;
	isShowDeleteBtn?: boolean;
	containerClassName?: string;
	actions?: ReactNode;
	loading?: boolean;
}

const Tabs: React.FC<IProps> = ({
	title,
	tabTitle,
	description,
	onDelete,
	isShowDeleteBtn = true,
	items = [],
	containerClassName,
	actions,
	loading = false,
}) => {
	const { t } = useTranslation('common');

	const [activeKey, setActiveKey] = useState<string>(
		items[0]?.key?.toString() || ''
	);

	const [ratios, setRatios] = useState<{ [key: string]: number }>({});
	const [isUserClicking, setIsUserClicking] = useState<boolean>(false); // flag to check if the user is clicking on the tab
	const mouseUpTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const updateRatios = useCallback((sectionId: string, ratio: number) => {
		setRatios((prev) => ({ ...prev, [sectionId]: ratio }));
	}, []);

	const handleTabClick = useCallback((key: string) => {
		setActiveKey(key);
		setIsUserClicking(true);
		const element = document.getElementById(key);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	}, []);

	const handleMouseUp = useCallback(() => {
		if (mouseUpTimeoutRef.current) {
			clearTimeout(mouseUpTimeoutRef.current);
		}
		mouseUpTimeoutRef.current = setTimeout(() => {
			setIsUserClicking(false);
		}, 1000);
	}, []);

	const handleScroll = useCallback(
		debounce(() => {
			if (!isUserClicking) {
				const entries = Object.entries(ratios);
				if (entries.length === 0) return;

				const sorted = entries.sort((a, b) => b[1] - a[1]);

				// Prioritize first element if scroll is near the top
				const scrollContainer = document.querySelector('.detail-tabs-content');
				if (scrollContainer) {
					const scrollTop = scrollContainer.scrollTop;
					if (scrollTop < 20) {
						setActiveKey(items[0]?.key?.toString() || '');
						return;
					}

					const scrollBottom =
						scrollContainer.scrollHeight -
						scrollContainer.scrollTop -
						scrollContainer.clientHeight;

					if (scrollBottom < 20) {
						setActiveKey(items[items.length - 1]?.key?.toString() || '');
						return;
					}
				}

				// Conversely, select the tab with the highest intersection ratio.
				const [topSection] = sorted;
				if (topSection) {
					setActiveKey(topSection[0]);
				}
			}
		}, 300),
		[ratios, isUserClicking, items]
	);

	return (
		<div className="tabs-container mx-[-36px]">
			<div className="tab-header w-full transition-shadow duration-300 bg-white  pt-3 px-9 border-b border-gray9 shadow-headerList relative z-0">
				<div className="row-between mb-3 h-9">
					<div className="h-6 flex items-center">
						<h2 className="text-xl font-semibold text-font2">{title}</h2>
						<Divider type="vertical" className="mx-2.5 h-2.5" />
						<span className="text-xs font-normal text-font8 self-end mr-1">
							{tabTitle?.id ? `[${tabTitle?.id}]` : ''}
						</span>
						<span className="text-lg font-medium text-font8">
							{tabTitle?.title || ''}
						</span>
					</div>
					<div className="flex items-center gap-2">
						{actions}
						{isShowDeleteBtn && onDelete && (
							<Button ghost type="primary" onClick={onDelete} isMainBtn>
								{t('delete')}
							</Button>
						)}
					</div>
				</div>
				<div className="detail-tabs-nav flex h-[35px]">
					{items.map((item, index) => (
						<div key={item.key} className="flex items-center">
							<div
								className={cn(
									'detail-tabs-nav-item min-w-[100px] flex justify-center items-center font-normal text-sm cursor-pointer px-2 leading-[35px] text-font3',
									activeKey === item.key
										? 'text-blue2 font-semibold border-b border-blue2 '
										: ''
								)}
								onClick={() => handleTabClick(item.key.toString())}
								onMouseUp={handleMouseUp}
							>
								{item.label}
							</div>
							{index !== items.length - 1 && (
								<Divider type="vertical" className="mx-5 h-[15px]" />
							)}
						</div>
					))}
				</div>
			</div>

			<div
				className={cn(
					'detail-tabs-content overflow-auto h-[calc(100vh-126px)] bg-blue5 px-9',
					loading && 'opacity-50',
					containerClassName
				)}
				onScroll={handleScroll}
			>
				{items.map((item, index) => (
					<TabContent
						key={item.key}
						id={item.key.toString()}
						updateRatios={updateRatios}
						isLast={index === items.length - 1}
						isFirst={index === 0}
					>
						{item.children}
					</TabContent>
				))}
			</div>
		</div>
	);
};

interface TabContentProps {
	id: string;
	children: React.ReactNode;
	updateRatios: (id: string, ratio: number) => void;
	isLast?: boolean;
	isFirst?: boolean;
}

const TabContent: React.FC<TabContentProps> = ({
	id,
	children,
	updateRatios,
	isLast = false,
	isFirst = false,
}) => {
	const { ref } = useInView({
		threshold: [0, 0.5, 1],
		onChange: (_inView, entry) => {
			updateRatios(id, entry.intersectionRatio);
		},
	});

	return (
		<div
			ref={ref}
			id={id}
			className={cn(isFirst && 'mt-5', !isLast && 'mb-[10px]')}
		>
			{children}
		</div>
	);
};

export default memo(Tabs);
